import React, { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * `common/MobileBottomSheet` (M-UX-6.1b, `plan-ux-6.1b-bottomsheet.md` §1.1).
 *
 * Generische Bottom-Sheet-Primitive für Mobil/Tablet (`lg:hidden`), extrahiert
 * aus der bisherigen `FabActionSheet`-Mechanik (`layout/MobileTabBar.tsx`).
 * Portal-basiert (`document.body`) — entkommt jedem `sticky`/`overflow`-
 * Ancestor (z. B. dem `sticky top-0 z-50`-Header-Wrapper in `MainLayout`).
 *
 * Verhaltens-Vertrag (bindend, §1.1):
 *  - `open===false` ⇒ `null`.
 *  - `role="dialog"`, `aria-modal="true"`, `aria-labelledby` auf den Titel.
 *  - Body-Scroll-Lock solange offen (Muster `common/Modal`).
 *  - Handgeschriebener Fokus-Trap (Tab/Shift+Tab-Zyklus) + Escape schließt.
 *  - Fokus-Return: `document.activeElement` wird beim Öffnen gemerkt und beim
 *    Schließen wiederhergestellt — der AUFRUFER ist dafür verantwortlich, dass
 *    sein Trigger-Element beim Öffnen tatsächlich fokussiert ist (Klick allein
 *    fokussiert Buttons NICHT verlässlich — weder in Safari noch in jsdom/
 *    Jest; Trigger sollten daher explizit `element.focus()` vor dem
 *    Öffnen aufrufen, s. `MobileTabBar.tsx` FAB/„Mehr"-Handler).
 *  - Backdrop-Tap schließt; Klick INS Panel schließt NICHT.
 *  - Interner Scrollport (`fb-sheet-scroll`) für langen Inhalt, Panel selbst
 *    ≤ `min(85dvh, 100%)`.
 *  - Grabber-Kopf (Grabber + Titel, NICHT der Scrollport) unterstützt Drag-to-
 *    dismiss: Loslassen bei > 80px Verschiebung schließt, sonst Snap-back.
 *    Bei `prefers-reduced-motion` kein visuelles Mitführen — nur der
 *    Schwellwert-Dismiss bleibt aktiv.
 */
export interface MobileBottomSheetProps {
    /** Controlled — Auf-/Zu-Zustand liegt beim Aufrufer. */
    open: boolean;
    /** Backdrop-Tap, ESC, Drag-Dismiss. */
    onClose: () => void;
    /** i18n-übersetzt vom Aufrufer; rendert als `<h2 id={titleId}>` → `aria-labelledby`. */
    title: string;
    /** Inhalt, landet im internen Scrollport. */
    children: ReactNode;
    /** Default `'mobile-bottom-sheet'`, sitzt auf dem `role="dialog"`-Panel. */
    'data-testid'?: string;
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Sichtbarkeits-gefilterte Fokus-Kandidaten innerhalb des Panels (Muster `common/Modal`). */
function getFocusable(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
    );
}

/** Ab dieser vertikalen Verschiebung (px) schließt ein Loslassen am Grabber-Kopf das Sheet. */
const DRAG_DISMISS_THRESHOLD_PX = 80;

function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function MobileBottomSheet({
    open,
    onClose,
    title,
    children,
    'data-testid': dataTestId = 'mobile-bottom-sheet',
}: MobileBottomSheetProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const titleId = useId();

    // `onClose` ist häufig eine frische Inline-Arrow des Aufrufers — analog
    // `common/Modal` NICHT als Effekt-Dependency verwenden (Re-Trigger-Loop-
    // Risiko bei jedem Parent-Rerender während offen).
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    // Drag-to-dismiss: vertikaler Offset (px) während des Ziehens am Grabber-Kopf.
    const [dragY, setDragY] = useState(0);
    const dragStateRef = useRef<{ startY: number; dragging: boolean }>({ startY: 0, dragging: false });

    useEffect(() => {
        if (!open) return undefined;

        previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
        dragStateRef.current = { startY: 0, dragging: false };
        setDragY(0);

        // Body-Scroll-Lock.
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // Initialer Fokus: erstes fokussierbares Element im Panel, sonst das Panel selbst.
        const panel = panelRef.current;
        if (panel) {
            const focusable = getFocusable(panel);
            (focusable[0] ?? panel).focus();
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (event.key === 'Tab' && panelRef.current) {
                const focusable = getFocusable(panelRef.current);
                if (focusable.length === 0) {
                    event.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
            previouslyFocusedRef.current?.focus();
        };
    }, [open]);

    const handleGrabPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        dragStateRef.current = { startY: event.clientY, dragging: true };
        (event.currentTarget as unknown as { setPointerCapture?: (id: number) => void }).setPointerCapture?.(
            event.pointerId,
        );
    };

    const handleGrabPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.dragging) return;
        // Reduced-motion: kein visuelles Mitführen, nur der Schwellwert-Dismiss
        // beim Loslassen bleibt aktiv (§1.1 Grabber-Zeile).
        if (prefersReducedMotion()) return;
        const dy = Math.max(0, event.clientY - dragStateRef.current.startY);
        setDragY(dy);
    };

    const handleGrabPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.dragging) return;
        const dy = Math.max(0, event.clientY - dragStateRef.current.startY);
        dragStateRef.current.dragging = false;
        if (dy > DRAG_DISMISS_THRESHOLD_PX) {
            setDragY(0);
            onClose();
            return;
        }
        setDragY(0);
    };

    if (!open) return null;

    const panelStyle: React.CSSProperties = {
        maxHeight: 'min(85dvh, 100%)',
        ...(dragY > 0 ? { transform: `translateY(${dragY}px)`, transition: 'none' } : {}),
    };

    return createPortal(
        <div className="fixed inset-0 z-50 lg:hidden">
            <div
                className="fb-mobile-sheet-overlay absolute inset-0 bg-black/70 backdrop-blur-[2px]"
                aria-hidden="true"
                onClick={onClose}
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                data-testid={dataTestId}
                style={panelStyle}
                className="fb-mobile-sheet-panel fb-sheet-drag-snap absolute inset-x-0 bottom-0 flex
                           flex-col rounded-t-3xl border-t border-white/[0.08] bg-surface-raised
                           pb-[max(0.5rem,env(safe-area-inset-bottom))] focus:outline-none"
            >
                <div
                    className="flex shrink-0 cursor-grab touch-none select-none flex-col items-center pt-3"
                    onPointerDown={handleGrabPointerDown}
                    onPointerMove={handleGrabPointerMove}
                    onPointerUp={handleGrabPointerUp}
                    onPointerCancel={handleGrabPointerUp}
                >
                    <span className="h-1.5 w-9 rounded-full bg-surface-hover" aria-hidden="true" />
                    <h2
                        id={titleId}
                        className="px-5 pb-1 pt-3 text-[13px] font-semibold uppercase tracking-wider
                                   text-muted"
                    >
                        {title}
                    </h2>
                </div>
                <div className="fb-sheet-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
                    {children}
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default MobileBottomSheet;
